import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Editor } from "@tiptap/react";
import { Box, SelectChangeEvent, Typography } from "@mui/material";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";

import { RevbellSelect } from "../../../components/Inputs/RevbellSelect";

type Alignement = "left" | "center" | "right" | "justify";

const Options = [
  {
    value: "left",
    label: (
      <Box sx={{ display: "flex", gap: 1, alignContent: "center" }}>
        <FormatAlignLeftIcon sx={{ alignContent: "center" }} />
        <Typography>
          <FormattedMessage id="LEFT" />
        </Typography>
      </Box>
    ),
  },
  {
    value: "center",
    label: (
      <Box sx={{ display: "flex", gap: 1 }}>
        <FormatAlignCenterIcon />
        <Typography>
          <FormattedMessage id="CENTER" />
        </Typography>
      </Box>
    ),
  },
  {
    value: "right",
    label: (
      <Box sx={{ display: "flex", gap: 1 }}>
        <FormatAlignRightIcon />
        <Typography>
          <FormattedMessage id="RIGHT" />
        </Typography>
      </Box>
    ),
  },
  {
    value: "justify",
    label: (
      <Box sx={{ display: "flex", gap: 1 }}>
        <FormatAlignJustifyIcon />
        <Typography>
          <FormattedMessage id="JUSTIFY" />
        </Typography>
      </Box>
    ),
  },
];

const AlignSelector = ({ editor }: { editor: Editor }) => {
  const [alignement, setAlignement] = useState<Alignement>("left");

  useEffect(() => {
    if (!editor) return;

    const updateTextAlign = () => {
      const alignments: Alignement[] = ["left", "center", "right", "justify"];
      const currentAlign = alignments.find((align) =>
        editor.isActive({ textAlign: align })
      );
      setAlignement(currentAlign || "left");
    };

    editor.on("selectionUpdate", updateTextAlign);
    editor.on("transaction", updateTextAlign);

    return () => {
      editor.off("selectionUpdate", updateTextAlign);
      editor.off("transaction", updateTextAlign);
    };
  }, [editor]);

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as Alignement;
    setAlignement(value);
    editor.chain().focus().setTextAlign(value).run();
  };

  return (
    <RevbellSelect
      defaultValue={"left"}
      value={alignement}
      options={Options}
      onChange={handleChange}
      sx={{
        width: "150px",
        "&.MuiInputBase-root": {
          backgroundColor: "background.input",
        },
        "& .MuiSelect-select": { padding: "4px 32px 4px 15px" },
      }}
    />
  );
};

export default AlignSelector;
